<template>
  <div class="p-3 md:p-6">
    <div v-if="loading">
      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card v-for="n in 3" :key="n">
          <CardContent class="p-4 flex flex-col gap-3">
            <Skeleton class="w-24 h-16 rounded-lg" />
            <div class="space-y-2">
              <Skeleton class="h-4 w-[200px]" />
              <Skeleton class="h-4 w-[150px]" />
            </div>
            <Skeleton class="h-16 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>

    <div v-else-if="error" class="text-destructive">
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{{ error }}</AlertDescription>
      </Alert>
      <Button variant="outline" @click="loadApplications" class="mt-4">
        Retry
      </Button>
    </div>

    <div v-else class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      <Card v-for="site in applicationList" :key="site.id"
        class="cursor-pointer hover:border-primary/40 hover:shadow-md transition-all group"
        @click="navigateToApp(site)">
        <CardHeader class="flex flex-row items-start justify-between p-4 pb-2">
          <div class="flex gap-4">
            <Avatar v-if="site.thumbUrl" class="w-24 h-16 rounded-md">
              <AvatarImage :src="site.thumbUrl" />
              <AvatarFallback>{{ site.name.charAt(0) }}</AvatarFallback>
            </Avatar>
            <div>
              <CardTitle class="text-subtitle font-normal group-hover:text-primary transition-colors">{{ site.name }}</CardTitle>
              <CardDescription class="text-body-sm font-normal">{{ site.maindomain }}</CardDescription>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="icon" class="h-8 w-8" @click.stop>
                <MoreHorizontal class="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem @click.stop="navigateToApp(site)">
                <FileCode class="h-4 w-4 mr-2" /> Edit Templates
              </DropdownMenuItem>
              <DropdownMenuItem @click.stop="navigateToApp(site)">
                <Settings2 class="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem class="text-destructive" @click.stop="deleteApp(site)">
                <Trash2 class="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent class="p-4 pt-0">
          <div class="h-[100px]" v-if="site.settings && site.settings.uploadsByDay">
            <LineChart :data="extractCounts(site.settings.uploadsByDay)" />
          </div>
        </CardContent>
        <CardFooter class="flex justify-between p-4 pt-0 text-sm text-muted-foreground">
	{{ site.description }}
        </CardFooter>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu'
import { MoreHorizontal, FileCode, Settings2, Trash2 } from 'lucide-vue-next';
import LineChart from '@/components/LineChart.vue';
import { useApplications } from '@/services/Applications';
import type { ApplicationInfo, DailyUpload } from '@/types/Applications';
import { toast } from 'vue-sonner';

const router = useRouter();
const { getApplicationList } = useApplications();
const vhostId = inject('vhostId', ref(''));
const applicationList = ref<ApplicationInfo[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

function navigateToApp(site: ApplicationInfo) {
	router.push({ name: 'Application', params: { appId: site.id } });
}

function deleteApp(site: ApplicationInfo) {
	toast.error(`Delete "${site.name}" — not yet implemented`)
}

function extractCounts(data: DailyUpload[]): number[] {
	  return data.map(item => item.count);
}
async function loadApplications() {
  try {
    loading.value = true;
    error.value = null;
    applicationList.value = await getApplicationList(vhostId.value);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load applications';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadApplications();
});
</script>
